import React, { useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Field,
  Flex,
  Heading,
  Input,
  Message,
  Modal,
  toast,
} from '@workwhile/ui'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCompaniesQuery } from 'queries/companies'
import {
  createTimeClockFlowSchema,
  CreateTimeClockFlowSchema,
} from '../schemas/createTimeClock'
import { useCreateTimeClockFlowContextValue } from '../context/CreateTimeClockFlowProvider'
import { useTimeClocksContextValue } from '../../../context/TimeClocksProvider'
import { useTimeClocksWorkflowsContextValue } from '../../../context/TimeClocksWorkflowsProvider'
import { LocationPicker } from '../../../components/LocationPicker'

export const CreateTimeClockFlowMain = () => {
  const {
    createTimeClockFlow: { isOpen, close },
  } = useTimeClocksWorkflowsContextValue()

  const {
    state: { companyId: filteredCompanyId, locationId: filteredLocationId },
  } = useTimeClocksContextValue()

  const {
    state: {
      mutationState: { isPending, errorMessage },
    },
    actions: { submit, reset: resetMutation },
  } = useCreateTimeClockFlowContextValue()

  const { data: companies } = useCompaniesQuery()

  const companyLabel = useMemo(() => {
    const company = companies?.find(
      (candidate) => candidate.id === filteredCompanyId
    )
    if (!company) return filteredCompanyId ? `Company ${filteredCompanyId}` : ''
    return `${company.name} (${company.id})`
  }, [companies, filteredCompanyId])

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    control,
  } = useForm<CreateTimeClockFlowSchema>({
    resolver: zodResolver(createTimeClockFlowSchema),
    defaultValues: {
      companyId: undefined,
      locationId: undefined,
      vendorClockId: '',
      manufacturer: 'Compumatic',
      model: 'CFR2020',
    },
  })

  useEffect(() => {
    if (!isOpen) return

    reset({
      companyId: filteredCompanyId ?? undefined,
      locationId: filteredLocationId ?? undefined,
      vendorClockId: '',
      manufacturer: 'Compumatic',
      model: 'CFR2020',
    })
  }, [isOpen, filteredCompanyId, filteredLocationId, reset])

  const handleClose = () => {
    reset()
    resetMutation()
    close()
  }

  const onSubmit = handleSubmit((data) => {
    submit(data, {
      onSuccess: () => {
        toast.success('Clock registered', {
          position: 'top-center',
        })
        handleClose()
      },
    })
  })

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      position="top"
      loading={isPending}
      showCancel
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Register
          </Button>
        </Flex>
      }
    >
      <Box
        as="form"
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
          Register clock
        </Heading>

        <Box mt={4}>Company</Box>
        <Box color="lightText">{companyLabel}</Box>

        <Box mt={4}>
          <Controller
            control={control}
            name="locationId"
            render={({ field, fieldState }) => (
              <LocationPicker
                value={field.value}
                onChange={field.onChange}
                companyId={filteredCompanyId}
                error={fieldState.error?.message?.toString()}
              />
            )}
          />
        </Box>

        <Box mt={4}>
          <Field
            label="Vendor Clock ID"
            error={errors.vendorClockId?.message?.toString()}
          >
            <Input
              aria-label="Vendor clock id"
              placeholder="e.g. CM-00123"
              {...register('vendorClockId')}
            />
          </Field>
        </Box>

        <Box mt={4}>
          <Field
            label="Manufacturer"
            error={errors.manufacturer?.message?.toString()}
          >
            <Input aria-label="Manufacturer" {...register('manufacturer')} />
          </Field>
        </Box>

        <Box mt={4}>
          <Field label="Model" error={errors.model?.message?.toString()}>
            <Input aria-label="Model" {...register('model')} />
          </Field>
        </Box>

        {errorMessage ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errorMessage}
          </Message>
        ) : null}
      </Box>
    </Modal>
  )
}
